/**
 * Centralized SDK Configuration
 *
 * This module provides a single source of truth for all SDK configuration
 * values including API endpoints, timeouts, and feature settings.
 *
 * @module config
 */

import { configureLogger } from '../utils/logger';
import { AGE_VERIFIER, AGE_VERIFIER_MODELS } from '../constants';

// Build-time injected API URL (set via API_BASE_URL env var, falls back to production)
declare const API_BASE_URL: string;

// =============================================================================
// Type Definitions
// =============================================================================

/**
 * API endpoint configuration.
 */
export interface ApiEndpointsConfig {
  /** Requirements check endpoint */
  requirements: string;
  /** Age upload endpoint */
  ageUpload: string;
  /** Final verification endpoint */
  verify: string;
  /** Challenge creation endpoint */
  challenge: string;
  /** Liveness verification endpoint */
  livenessVerify: string;
  /** ID submission endpoint */
  submitId: string;
  /** Session status endpoint (with {sessionId} placeholder) */
  sessionStatus: string;
  /** OCR endpoints */
  ocr: {
    /** Presign URL endpoint */
    presign: string;
    /** Verify URLs endpoint */
    verifyUrls: string;
    /** OCR status endpoint (with {taskId} placeholder) */
    status: string;
  };
  /** Liveness endpoints */
  liveness: {
    /** Get challenge endpoint */
    challenge: string;
    /** Submit verification endpoint */
    verify: string;
  };
}

/**
 * Timeout configuration in milliseconds.
 */
export interface TimeoutsConfig {
  /** Default HTTP request timeout */
  default: number;
  /** Requirements API timeout */
  requirements: number;
  /** File upload timeout */
  upload: number;
  /** Individual polling request timeout */
  pollingRequest: number;
}

/**
 * Polling behavior configuration.
 */
export interface PollingConfig {
  /** Initial polling interval in ms */
  initialInterval: number;
  /** Maximum polling interval in ms */
  maxInterval: number;
  /** Backoff multiplier for exponential backoff */
  backoffFactor: number;
  /** Maximum attempts before giving up */
  maxAttempts: number;
  /** Jitter factor to randomize intervals (0-1) */
  jitter: number;
}

/**
 * Liveness detection configuration.
 */
export interface LivenessConfig {
  /** Number of random actions to pick for verification */
  actionCount: number;
  /** Maximum retry attempts for failed actions */
  maxAttempts: number;
  /** Maximum frames to capture */
  maxFrames: number;
  /** Interval between frame captures in ms */
  frameInterval: number;
}

/**
 * Camera configuration.
 */
export interface CameraConfig {
  /** Default facing mode */
  defaultFacingMode: 'user' | 'environment';
  /** Preferred video width */
  preferredWidth: number;
  /** Preferred video height */
  preferredHeight: number;
}

/**
 * Age verifier model configuration.
 *
 * Allows switching between different trained models (e.g., EfficientNet-Lite0 vs B2)
 * with different input sizes without code changes.
 */
export interface AgeVerifierConfig {
  /** Path to the ONNX model file (relative to models directory) */
  modelPath: string;
  /** Model input size (width & height, assumes square) */
  inputSize: number;
  /** ONNX input tensor name */
  inputName: string;
  /** ONNX output tensor name */
  outputName: string;
  /** ONNX execution provider */
  executionProvider: 'wasm' | 'webgpu' | 'webgl';
  /** Risk tolerance for verification (default: 0.02 = 2%) */
  riskTolerance: number;
  /** Enable debug logging */
  debug: boolean;
}

/**
 * Complete SDK configuration.
 */
export interface SDKConfig {
  /** API configuration */
  api: {
    /** Base URL for API requests (no trailing slash) */
    baseUrl: string;
    /** API endpoint paths */
    endpoints: ApiEndpointsConfig;
    /** Timeout settings */
    timeouts: TimeoutsConfig;
  };
  /** Polling configuration */
  polling: PollingConfig;
  /** Liveness detection configuration */
  liveness: LivenessConfig;
  /** Camera configuration */
  camera: CameraConfig;
  /** Age verifier model configuration */
  ageVerifier: AgeVerifierConfig;
  /**
   * Enable debug logging.
   * When true, all log levels (debug, info, warn, error) are shown in console.
   * When false (default), only warnings and errors are shown.
   */
  debug: boolean;
}

// =============================================================================
// Default Configuration
// =============================================================================

/**
 * Default API endpoints.
 * All paths are relative to the base URL.
 */
export const DEFAULT_ENDPOINTS: ApiEndpointsConfig = {
  requirements: '/v1/requirements/check',
  ageUpload: '/v1/age/upload',
  verify: '/v1/verify',
  challenge: '/v1/challenge',
  livenessVerify: '/v1/verify_liveness',
  submitId: '/v1/submit_id',
  sessionStatus: '/v1/status/{sessionId}',
  ocr: {
    presign: '/v1/ocr/presign',
    verifyUrls: '/v1/ocr/verify-urls',
    status: '/v1/ocr/status',
  },
  liveness: {
    challenge: '/v1/liveness/challenge',
    verify: '/v1/liveness/verify',
  },
};

/**
 * Default timeout values in milliseconds.
 */
export const DEFAULT_TIMEOUTS: TimeoutsConfig = {
  default: 30000,
  requirements: 10000,
  upload: 30000,
  pollingRequest: 10000,
};

/**
 * Default polling configuration.
 */
export const DEFAULT_POLLING: PollingConfig = {
  initialInterval: 1000,
  maxInterval: 30000,
  backoffFactor: 1.5,
  maxAttempts: 60,
  jitter: 0.1,
};

/**
 * Default liveness detection configuration.
 */
export const DEFAULT_LIVENESS: LivenessConfig = {
  actionCount: 2,
  maxAttempts: 3,
  maxFrames: 12,
  frameInterval: 150,
};

/**
 * Default camera configuration.
 */
export const DEFAULT_CAMERA: CameraConfig = {
  defaultFacingMode: 'user',
  preferredWidth: 1280,
  preferredHeight: 720,
};

/**
 * Default age verifier configuration.
 *
 * Currently uses the EfficientNet-B2 model (260×260 input) which is well-tested.
 *
 * To try the new EfficientNet-Lite0 model (224×224), override with:
 * ```typescript
 * setGlobalConfig({
 *   ageVerifier: {
 *     ...AGE_VERIFIER_MODELS.EFFICIENTNET_LITE0_FP16,
 *     // NOTE: EfficientNet-Lite0 may require different normalization!
 *   }
 * });
 * ```
 */
export const DEFAULT_AGE_VERIFIER: AgeVerifierConfig = {
  // EfficientNet-Lite0 model (224×224) - smaller and faster
  modelPath: AGE_VERIFIER_MODELS.EFFICIENTNET_LITE0_FP16.modelPath,
  inputSize: AGE_VERIFIER_MODELS.EFFICIENTNET_LITE0_FP16.inputSize,
  inputName: AGE_VERIFIER_MODELS.EFFICIENTNET_LITE0_FP16.inputName,
  outputName: AGE_VERIFIER_MODELS.EFFICIENTNET_LITE0_FP16.outputName,
  executionProvider: AGE_VERIFIER.DEFAULT_EXECUTION_PROVIDER,
  riskTolerance: AGE_VERIFIER.DEFAULT_RISK_TOLERANCE,
  debug: true, // Enable debug logging to show cropped images and results
};

/**
 * Complete default SDK configuration.
 */
export const DEFAULT_SDK_CONFIG: SDKConfig = {
  api: {
    baseUrl: API_BASE_URL,
    endpoints: DEFAULT_ENDPOINTS,
    timeouts: DEFAULT_TIMEOUTS,
  },
  polling: DEFAULT_POLLING,
  liveness: DEFAULT_LIVENESS,
  camera: DEFAULT_CAMERA,
  ageVerifier: DEFAULT_AGE_VERIFIER,
  debug: false,
};

// =============================================================================
// Configuration Helpers
// =============================================================================

/**
 * Deep partial type for nested configuration overrides.
 */
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

/**
 * Deep merge two objects, with source values overriding target values.
 *
 * @param target - Base object
 * @param source - Override object
 * @returns Merged object
 */
function deepMerge<T extends object>(target: T, source: DeepPartial<T>): T {
  const result = { ...target } as T;

  for (const key of Object.keys(source) as Array<keyof T>) {
    const sourceValue = source[key];
    const targetValue = target[key];

    if (
      sourceValue !== undefined &&
      typeof sourceValue === 'object' &&
      sourceValue !== null &&
      !Array.isArray(sourceValue) &&
      typeof targetValue === 'object' &&
      targetValue !== null &&
      !Array.isArray(targetValue)
    ) {
      // Recursively merge nested objects
      (result as Record<string, unknown>)[key as string] = deepMerge(
        targetValue as object,
        sourceValue as DeepPartial<object>
      );
    } else if (sourceValue !== undefined) {
      // Override primitive values
      (result as Record<string, unknown>)[key as string] = sourceValue;
    }
  }

  return result;
}

/**
 * Create SDK configuration with optional overrides.
 *
 * @param overrides - Partial configuration to merge with defaults
 * @returns Complete SDK configuration
 *
 * @example
 * ```typescript
 * // Use all defaults
 * const config = createSDKConfig();
 *
 * // Override API base URL
 * const devConfig = createSDKConfig({
 *   api: { baseUrl: 'http://localhost:8000' }
 * });
 *
 * // Override multiple values
 * const customConfig = createSDKConfig({
 *   api: {
 *     baseUrl: 'https://staging.appbouncer.com',
 *     timeouts: { default: 60000 }
 *   },
 *   liveness: { actionCount: 3 }
 * });
 * ```
 */
export function createSDKConfig(overrides?: DeepPartial<SDKConfig>): SDKConfig {
  if (!overrides) {
    return { ...DEFAULT_SDK_CONFIG };
  }
  return deepMerge(DEFAULT_SDK_CONFIG, overrides);
}

/**
 * Build a full URL from base URL and endpoint path.
 *
 * @param baseUrl - API base URL
 * @param endpoint - Endpoint path (should start with /)
 * @returns Full URL
 *
 * @example
 * ```typescript
 * buildUrl('https://api.appbouncer.com', '/v1/requirements/check');
 * // Returns: 'https://api.appbouncer.com/v1/requirements/check'
 * ```
 */
export function buildUrl(baseUrl: string, endpoint: string): string {
  // Remove trailing slash from base URL if present
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  // Ensure endpoint starts with /
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${base}${path}`;
}

/**
 * Build URL with path parameters replaced.
 *
 * @param baseUrl - API base URL
 * @param endpoint - Endpoint path with {param} placeholders
 * @param params - Object with parameter values
 * @returns Full URL with parameters replaced
 *
 * @example
 * ```typescript
 * buildUrlWithParams(
 *   'https://api.appbouncer.com',
 *   '/v1/status/{sessionId}',
 *   { sessionId: 'abc123' }
 * );
 * // Returns: 'https://api.appbouncer.com/v1/status/abc123'
 * ```
 */
export function buildUrlWithParams(
  baseUrl: string,
  endpoint: string,
  params: Record<string, string>
): string {
  let path = endpoint;
  for (const [key, value] of Object.entries(params)) {
    path = path.replace(`{${key}}`, encodeURIComponent(value));
  }
  return buildUrl(baseUrl, path);
}

// =============================================================================
// Singleton Configuration Instance
// =============================================================================

/**
 * Global SDK configuration instance.
 * Can be overridden at runtime using setGlobalConfig().
 */
let globalConfig: SDKConfig = { ...DEFAULT_SDK_CONFIG };

/**
 * Get the current global SDK configuration.
 */
export function getConfig(): SDKConfig {
  return globalConfig;
}

/**
 * Get the age verifier configuration.
 * Convenience function to access age verifier settings.
 */
export function getAgeVerifierConfig(): AgeVerifierConfig {
  return globalConfig.ageVerifier;
}

/**
 * Set the global SDK configuration.
 * This affects all SDK components that use getConfig().
 *
 * @param config - New configuration (will be merged with defaults)
 */
export function setGlobalConfig(config: DeepPartial<SDKConfig>): void {
  globalConfig = createSDKConfig(config);

  // Configure logger based on debug setting
  configureLogger({ debug: globalConfig.debug });
}

/**
 * Reset global configuration to defaults.
 */
export function resetConfig(): void {
  globalConfig = { ...DEFAULT_SDK_CONFIG };

  // Reset logger to default (non-debug) mode
  configureLogger({ debug: false });
}

/**
 * Get a specific endpoint URL.
 *
 * @param endpoint - Endpoint key path (e.g., 'requirements', 'ocr.presign')
 * @returns Full endpoint URL
 */
export function getEndpointUrl(
  endpoint:
    | keyof ApiEndpointsConfig
    | `ocr.${keyof ApiEndpointsConfig['ocr']}`
    | `liveness.${keyof ApiEndpointsConfig['liveness']}`
): string {
  const config = getConfig();
  const baseUrl = config.api.baseUrl;

  if (endpoint.startsWith('ocr.')) {
    const ocrKey = endpoint.slice(4) as keyof ApiEndpointsConfig['ocr'];
    return buildUrl(baseUrl, config.api.endpoints.ocr[ocrKey]);
  }

  if (endpoint.startsWith('liveness.')) {
    const livenessKey = endpoint.slice(9) as keyof ApiEndpointsConfig['liveness'];
    return buildUrl(baseUrl, config.api.endpoints.liveness[livenessKey]);
  }

  return buildUrl(baseUrl, config.api.endpoints[endpoint as keyof ApiEndpointsConfig] as string);
}
